import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { IoBookmarkOutline, IoBookmark, IoChevronBack, IoChevronForward } from "react-icons/io5";
import { useAuth } from "../../../contexts/AuthContext";
import { useProfileInfo } from "../../../contexts/ProfileInfoContext";
import ShareDropdown from "../../ShareDropdown";

const FeaturedJobs = ({
    featuredJobs,
    bookmarkedInternalJobs,
    shareDropdown,
    handleShareClick,
    handleCloseShare,
    handleBookmarkInternalJob,
    handleRemoveJobMatch,
}) => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { profileInfo } = useProfileInfo();
    const scrollContainerRef = useRef(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    //show featured jobs if user is logged in and employee
    const shouldShow = user && profileInfo?.role === 'Employee';

    useEffect(() => {
        const checkScrollability = () => {
            if (scrollContainerRef.current) {
                const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
                setCanScrollLeft(scrollLeft > 0);
                setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10); // 10px threshold
            }
        };

        // Initial check after a short delay to ensure DOM is ready
        const timeoutId = setTimeout(checkScrollability, 100);
        
        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer) {
            scrollContainer.addEventListener('scroll', checkScrollability);
            window.addEventListener('resize', checkScrollability);
        }

        return () => {
            clearTimeout(timeoutId);
            if (scrollContainer) {
                scrollContainer.removeEventListener('scroll', checkScrollability);
            }
            window.removeEventListener('resize', checkScrollability);
        };
    }, [featuredJobs]);

    // Don't show if user is not logged in/employer
    if (!shouldShow) {
        return null;
    }

    if (!featuredJobs || featuredJobs.length === 0) {
        return null;
    }

    const getGapSize = () => {
        if (typeof window === 'undefined') return 16;
        // gap-3 = 0.75rem = 12px on mobile, gap-4 = 1rem = 16px on larger screens
        return window.innerWidth >= 640 ? 16 : 12;
    };

    const handlePrevious = () => {
        if (scrollContainerRef.current) {
            const container = scrollContainerRef.current;
            const cardWidth = container.querySelector('.job-card')?.offsetWidth || 0;
            const gap = getGapSize();
            const scrollAmount = cardWidth + gap;
            container.scrollBy({
                left: -scrollAmount,
                behavior: 'smooth'
            });
        }
    };

    const handleNext = () => {
        if (scrollContainerRef.current) {
            const container = scrollContainerRef.current;
            const cardWidth = container.querySelector('.job-card')?.offsetWidth || 0;
            const gap = getGapSize();
            const scrollAmount = cardWidth + gap;
            container.scrollBy({
                left: scrollAmount,
                behavior: 'smooth'
            });
        }
    };

    return (
        <div className="bg-white px-4 sm:px-6 pt-0 pb-4 sm:pb-6 mb-6 -mt-4 sm:-mt-6">
            <div className="relative flex items-center justify-center mb-4 sm:mb-6">
                <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 text-center">Featured Jobs</h2>
                {(canScrollLeft || canScrollRight) && (
                    <div className="absolute right-0 flex items-center gap-1 sm:gap-2">
                        <button
                            onClick={handlePrevious}
                            disabled={!canScrollLeft}
                            className={`p-1.5 sm:p-2 rounded-full transition-colors ${
                                canScrollLeft
                                    ? "bg-gray-100 hover:bg-gray-200 text-gray-700"
                                    : "bg-gray-50 text-gray-300 cursor-not-allowed"
                            }`}
                            aria-label="Previous jobs"
                        >
                            <IoChevronBack className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                        <button
                            onClick={handleNext}
                            disabled={!canScrollRight}
                            className={`p-1.5 sm:p-2 rounded-full transition-colors ${
                                canScrollRight
                                    ? "bg-gray-100 hover:bg-gray-200 text-gray-700"
                                    : "bg-gray-50 text-gray-300 cursor-not-allowed"
                            }`}
                            aria-label="Next jobs"
                        >
                            <IoChevronForward className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                    </div>
                )}
            </div>
            <div 
                ref={scrollContainerRef}
                className="flex gap-3 sm:gap-4 overflow-x-auto scrollbar-hide scroll-smooth pb-2 -mx-4 px-4 sm:-mx-6 sm:px-6"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
                {featuredJobs.slice(0, 3).map((job) => (
                    <div
                        key={job.id}
                        className="job-card bg-white border border-gray-200 rounded-xl p-3 sm:p-4 flex-shrink-0"
                        style={{
                            minWidth: 'calc(100% - 1rem)',
                            width: 'calc(100% - 1rem)',
                        }}
                    >
                        <div className="flex items-start justify-between">
                            <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2 flex-wrap">
                                    <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full">
                                        {job.matchPercentage}% Job Match
                                    </span>
                                    {job.isSponsored && (
                                        <span className="bg-gray-900 text-white text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full">
                                            Sponsored
                                        </span>
                                    )}
                                </div>
                                <h3 className="font-semibold text-gray-900 mb-1">{job.title}</h3>
                                <div className="text-sm text-gray-600 mb-2">
                                    {job.location && job.location.match(/,\s*South\s+Africa$/i) ? (
                                        <>
                                            <span>{job.company} {job.location.replace(/,\s*South\s+Africa$/i, '').trim()}</span>
                                            <br />
                                            <span>South Africa</span>
                                        </>
                                    ) : (
                                        <span>{job.company} {job.location}</span>
                                    )}
                                </div>
                                <p className="text-xs text-gray-500">Posted {job.postedDays} days ago</p>
                            </div>
                            <div className="flex flex-col items-end gap-4">
                                <div className="flex items-center gap-1">
                                    <div className="relative">
                                        <button className="p-1 hover:bg-gray-100 rounded" onClick={(e) => handleShareClick(job, e)}>
                                            <img src="/Share.svg" alt="Share" className="w-4 h-4" />
                                        </button>
                                        {shareDropdown.open && shareDropdown.job?.id === job.id && (
                                            <ShareDropdown
                                                url={`${window.location.origin}/job/${job.id}`}
                                                title={job.title}
                                                text={`${job.title} at ${job.company}${job.location ? ` • ${job.location}` : ""}`}
                                                onClose={handleCloseShare}
                                            />
                                        )}
                                    </div>
                                    <button 
                                        className="p-1 hover:bg-gray-100 rounded"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleBookmarkInternalJob(job.id);
                                        }}
                                        title={bookmarkedInternalJobs.includes(job.id) ? "Remove bookmark" : "Bookmark job"}
                                    >
                                        {bookmarkedInternalJobs.includes(job.id) ? (
                                            <IoBookmark className="w-4 h-4 text-black" />
                                        ) : (
                                            <IoBookmarkOutline className="w-4 h-4 text-gray-600" />
                                        )}
                                    </button>
                                    <button 
                                        className="p-1 hover:bg-gray-100 rounded"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleRemoveJobMatch(job.id);
                                        }}
                                        title="Remove job match"
                                    >
                                        <img src="/cross.svg" alt="Close" className="w-4 h-4" />
                                    </button>
                                </div>
                                <button 
                                    onClick={() => {
                                        if (job.originalJob && job.originalJob.application_url) {
                                            window.open(job.originalJob.application_url, '_blank');
                                        } else {
                                            navigate(`/job/${job.id}`);
                                        }
                                    }}
                                    className="bg-black text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-gray-800 mt-2"
                                >
                                    View Job
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            <style jsx>{`
                .scrollbar-hide::-webkit-scrollbar {
                    display: none;
                }
                
                @media (min-width: 640px) {
                    .job-card {
                        min-width: calc(50% - 0.75rem) !important;
                        width: calc(50% - 0.75rem) !important;
                    }
                }
                
                @media (min-width: 1024px) {
                    .job-card {
                        min-width: calc(33.333% - 1rem) !important;
                        width: calc(33.333% - 1rem) !important;
                    }
                }
            `}</style>
        </div>
    );
};

export default FeaturedJobs;
